import type { TargettedEvent } from '@suntex.core/animation';
import { UIUtil } from '@suntex.core/animation';
import { $Color, type Color } from '@suntex.core/color';
import type { GameFrame } from '@suntex.modules/core';
import { ValueText } from '@suntex.modules/core';

import { globalToLocalXY } from '../compat';
import type { VifRulesGameStage, VifTestWindowCell } from '../types';

import type { BoardGeometry } from './BoardGeometry';
import { BoardGameEngine } from './boardGameEngine';
import {
  pickDistinctIndices,
  shuffleAvoidingFixedPositions,
} from './boardMath';
import { cellAt } from './grid';

/**
 * Shared base for the 3xN "rules" tile-matching games (RulesAddSub,
 * RulesMulDiv). Carries every genuinely rules-shared concrete member (same
 * split as `BlockBoardEngine`/`BoardGameEngine`) plus the per-game
 * differences as protected overridable hooks. `VIFRulesAddSub` and
 * `VIFRulesMulDiv` both extend this directly, overriding the hooks with
 * their own bodies and reading/writing engine state (`this.topEQs`, etc.) as
 * plain `this` access.
 */
export abstract class RulesBoardEngine extends BoardGameEngine<VifRulesGameStage> {
  protected baseGameFlag = 0;
  protected gmeter!: createjs.MovieClip;
  protected rmeter!: createjs.MovieClip;

  protected intLayersInGame = 1;
  protected scaleVar = 1;
  protected topScaleVar = 2;
  protected preString = '';
  protected intColumns = 3;

  protected blnFirstTimeLayer = [1, 1, 1, 1];
  protected readonly blnChosen: number[][] = [[0], [0], [0], [0]];

  protected blnFirstTime = true;

  protected get correctColor(): Color {
    return $Color.from('#000000');
  }

  protected get gridRows(): number {
    return 3;
  }

  protected get gridCols(): number {
    return this.intColumns;
  }

  /**
   * `RulesBoardEngine` overrides every base method that would consult
   * `this.geometry` (`getMouseCoordinates`/`onBoardMouseMove`) with its own
   * body, so this getter is never actually read; it exists only to satisfy
   * `BoardGameEngine`'s abstract member. The rules boards' real geometry
   * numbers still live in each rules game's hooks and are unchanged by this
   * step.
   */
  protected get geometry(): BoardGeometry {
    throw new Error(
      'RulesBoardEngine.geometry is unused - getMouseCoordinates/onBoardMouseMove are overridden directly.',
    );
  }

  protected tileBackFrame(): number {
    return this.isAltFrameGameFlag(this.gameFlag) ? 4 : 2;
  }

  protected onActivateAfterReset(): void {
    if (this.useEarlyEnableStartButton()) {
      this.enableStartButton();
    } else {
      if (this.intLayer > this.intLayersInGame) {
        this.enableStartButton();
        this.intLayer = 1;
      } else {
        this.nextProblem();
      }
    }
  }

  protected bottomHalfCorrectFrame(): number {
    return this.isAltFrameGameFlag(this.gameFlag) ? 5 : 3;
  }

  protected playCorrectSound(): void {
    this.playCorrectSoundHook();
  }

  protected tEqValueAt(index: number): string | number {
    return this.tEqValues[index]!;
  }

  protected checkForEnd(): void {
    this.board.enabled = false;
    this.checkForEndBody();
  }

  protected getMouseCoordinates(e: createjs.MouseEvent): {
    row: number;
    col: number;
  } {
    return this.getMouseCoordinatesHook(e);
  }

  protected tradePiece(): void {
    if (
      this.dragging &&
      this.board.enabled &&
      this.currCol != null &&
      this.currRow != null
    ) {
      const tradingText = cellAt(this.board, this.currRow, this.currCol).t
        .textb;
      const tValue =
        this.tileOrigAnswers[this.intColumns * this.currRow + this.currCol]!;
      const myValue =
        this.tileOrigAnswers[
          this.intColumns * this.startRow! + this.startCol!
        ]!;
      this.tileOrigAnswers[this.intColumns * this.startRow! + this.startCol!] =
        tValue;
      this.tileOrigAnswers[this.intColumns * this.currRow + this.currCol] =
        myValue;
      ValueText.displayText(
        tradingText,
        this.preString + myValue,
        this.scaleVar,
      );
      const draggingText = this.draggingPiece!.textb;
      ValueText.displayText(
        draggingText,
        this.preString + tValue,
        this.scaleVar,
      );
      this.tileLocked[this.currRow]![this.currCol] = 1;
      if (
        myValue == this.tEqValues[this.intColumns * this.currRow + this.currCol]
      ) {
        this.releasePiece();
        this.board.enabled = false;
        this.onCorrectMatch(tValue);
      } else {
        this.releasePiece();
        this.board.enabled = false;
        this.incorrectAnswer();
      }
    }
  }

  protected releasePiece(): void {
    if (this.dragging && this.board.enabled) {
      UIUtil.hide(this.mobilePiece, 300);
      UIUtil.show(this.draggingPiece, 300);
      this.fadeAllTiles(1, 0);
      this.dragging = false;
      this.draggingPiece = null;
    }
  }

  /* Hooks where Rules differs from the base defaults */

  protected onStopGameBeginHook(): void {
    this.onStopGameBegin();
  }

  protected computeCompletionOperand(): number {
    return this.computeOperation(this.gameFlag);
  }

  protected onTimedOut(): void {
    this.timeOutReset();
  }

  protected canTradePiece(): boolean {
    return this.canTradePieceHook();
  }

  protected onResettingTile(tile: VifTestWindowCell): void {
    tile.t.jiggleEnabled = false;
  }

  protected onBonusSoundQueue(): void {
    this.onBonusSoundQueueHook();
  }

  protected initGame(): void {
    const { params } = this.args;
    this.gameFlag = this.computeGameFlag(this.gameFrame, params.moduleVariant);
    this.playerId = params.playerId;
    this.vifStage = params.stage;

    this.baseGameFlag = this.computeBaseGameFlag(this.gameFlag);

    this.gameFrame.roundsInModule = this.rounds[this.baseGameFlag]!;
    this.gameFrame.gamesPerRound = this.GPR[this.baseGameFlag]!;

    this.initGameFlag();

    const rules = this.gameStage.rulesclip;
    const examples = rules.examples;
    const eqtn = rules.equatn;
    const rText = rules.ruleHdr;
    const rHeader = rules.ruleTxt;

    UIUtil.setFrame(examples, this.gameFlag + 1);
    UIUtil.setFrame(eqtn, this.gameFlag + 1);
    UIUtil.setFrame(rText, this.gameFlag + 1);
    UIUtil.setFrame(rHeader, this.gameFlag + 1);

    this.onRulesFramesSet();

    this.gameFrame.disableMoreBtn = true;
    this.gameFrame.disableNextBtn = true;

    UIUtil.show(this.gameStage.startButton);
    this.gmeter = this.gameStage.gameMeter;
    this.rmeter = this.gameStage.roundMeter;
    UIUtil.show(this.gameStage.gameMeter);

    this.initialUiSetup();

    UIUtil.hide(this.gameStage);
    this.initInterface();
    this.gameFrame.initGame(
      this.startGame.bind(this),
      this.stopGame.bind(this),
    );
  }

  protected startGame(): void {
    this.loadSounds();
    UIUtil.show(this.gameStage, 500);

    this.blnFirstTime = true;

    this.startGameBody();

    this.setupGame();
  }

  protected setupGame(): void {
    this.intLayer = this.gameFrame.game;
    this.onSetupGameHook();
  }

  /* Interface */

  private initInterface() {
    UIUtil.createButton(
      this.gameStage.startButton,
      this.activateStartButton.bind(this),
      false,
      undefined,
      undefined,
    );
    this.initButtons();
    this.mobilePiece = this.gameStage.mPiece;
    this.board = this.gameStage.testWindow;
  }

  private activateStartButton(e: object) {
    window.startTimeOnTask();

    const btn = UIUtil.getEventTarget(e as TargettedEvent<unknown>);
    UIUtil.setFrame(btn, 2);
    UIUtil.disable(btn);
    this.nextProblem();
  }

  private timeOutReset() {
    this.resetAllTilesToFirstFrame();
    this.enableStartButton();
    this.resetAllTiles(this.timeOutResetActivates());
  }

  protected nextProblem(): void {
    if (this.startSeconds[this.gameFrame.round - 1] != 0) {
      if (this.clockTimer.isRunning()) {
        this.clockTimer.stop();
      }

      this.clockTimer.start();
      this.clockSeconds = this.startSeconds[this.gameFrame.round - 1]!;
      ValueText.whiten(this.gameStage.startButton.clockDisplay);
      ValueText.displayText(
        this.gameStage.startButton.clockDisplay,
        this.clockSeconds,
      );
    } else {
      UIUtil.hide(this.gameStage.startButton);
    }

    if (this.blnFirstTime) {
      this.blnFirstTime = false;
      this.onNextProblemFirstTime();
    }

    this.populateProblem();

    this.intCorrect = 0;

    this.tileLocked = [];
    for (let r = 0; r < 3; r++) {
      this.tileLocked.push(new Array<number>(this.intColumns).fill(0));
    }

    this.blocker = this.gameStage.blocker;
    this.blockerX = this.blocker.x;
    this.blockerY = this.blocker.y;
    this.blockerH = this.blocker.height;
    this.blockerW = this.blocker.width;
    UIUtil.fadeTo(this.blocker, 0, this.animMs);

    this.resetAllTilesToFirstFrame();

    const usedIndices = new Set<number>();
    this.blnChosen[this.intLayer - 1]!.forEach((v, idx) => {
      if (v === 1) {
        usedIndices.add(idx);
      }
    });
    const chosenIndices = pickDistinctIndices(
      this.bottomAnswers.length,
      this.intColumns * 3,
      Math.random,
      usedIndices,
    );
    for (const idx of chosenIndices) {
      this.blnChosen[this.intLayer - 1]![idx] = 1;
    }
    this.tEqValues = chosenIndices.map((idx) => this.bottomAnswers[idx]!);

    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < this.intColumns; col++) {
        const settingTile = cellAt(this.board, row, col);
        settingTile.t.jiggleEnabled = true;
        UIUtil.delay(this.jiggle.bind(this), this.animMs, [settingTile.t]);
        this.fillGridCell(
          row,
          col,
          chosenIndices[row * this.intColumns + col]!,
        );
      }
    }

    const selected = shuffleAvoidingFixedPositions(this.tEqValues, Math.random);

    for (let row = 0; row < this.gridRows; row++) {
      for (let col = 0; col < this.gridCols; col++) {
        const cell = cellAt(this.gameStage.testWindow, row, col);
        ValueText.displayText(
          cell.t.textb,
          this.preString + selected[row * this.intColumns + col],
          this.scaleVar,
        );
      }
    }

    this.tileOrigAnswers = selected;
    UIUtil.useHandCursor(this.board);
    UIUtil.onMouseMove(
      this.board,
      this.onBoardMouseDown.bind(this),
      this.onBoardMouseMove.bind(this),
      this.onBoardMouseUp.bind(this),
    );

    this.finalizeNextProblem();
  }

  protected onBoardMouseDown(e: createjs.MouseEvent): void {
    if (!this.dragging && this.board.enabled && this.canStartDrag()) {
      const coord = this.getMouseCoordinatesHook(e);

      if (
        coord.row >= 0 &&
        coord.row < this.gridRows &&
        coord.col >= 0 &&
        coord.col < this.gridCols
      ) {
        if (!this.tileLocked[coord.row]![coord.col]) {
          this.draggingPiece = cellAt(this.board, coord.row, coord.col).t;
          this.startRow = coord.row;
          this.startCol = coord.col;
          this.currRow = coord.row;
          this.currCol = coord.col;

          UIUtil.hide(this.draggingPiece);
          const point = UIUtil.getMousePosition(e);
          const xypoint = globalToLocalXY(
            this.mobilePiece.parent,
            point.x,
            point.y,
          );
          UIUtil.moveTo(this.mobilePiece, xypoint[0] + 30, xypoint[1] + 50);
          UIUtil.show(this.mobilePiece, 100);
          if (this.isAltFrameGameFlag(this.gameFlag)) {
            UIUtil.setFrame(this.mobilePiece, 2);
          }
          ValueText.displayText(
            this.mobilePiece.t.textb,
            this.preString +
              this.tileOrigAnswers[
                this.intColumns * this.currRow + this.currCol
              ],
            this.scaleVar,
          );
          this.dragging = true;

          if (this.draggingPiece) {
            this.onDragStart();
            UIUtil.bringToFront(this.mobilePiece.parent);
            UIUtil.scaleTo(this.mobilePiece, 2, this.animMs);
            UIUtil.fadeTo(this.mobilePiece, 0.8, this.animMs);
          }
        }
      }
    }
  }

  protected onBoardMouseMove(e: createjs.MouseEvent): void {
    if (this.dragging && this.board.enabled) {
      const point = UIUtil.getMousePosition(e);
      const boardPoint = UIUtil.getMousePosition(e, this.board);

      if (this.draggingPiece) {
        if (this.isPointOutOfBounds(boardPoint)) {
          this.releasePiece();
        } else {
          UIUtil.bringToFront(this.dragMoveBringToFrontTarget());

          const coord = this.computeHoverCell(boardPoint);

          const xypoint = globalToLocalXY(
            this.mobilePiece.parent,
            point.x,
            point.y,
          );
          UIUtil.moveTo(this.mobilePiece, xypoint[0] + 30, xypoint[1] + 50);

          if (this.currRow != coord.row || this.currCol != coord.col) {
            this.onHoverCellChange(coord.row, coord.col);
          }
        }
      }
    }
  }

  // `onBoardMouseUp` is inherited from `BoardGameEngine` - it calls
  // `this.getMouseCoordinates(e)` and `this.canTradePiece()`, both of which
  // this class overrides above to delegate to the rules hooks, so no local
  // override is needed here.

  protected stopClockTimer(): void {
    if (this.clockTimer.isRunning()) {
      this.clockTimer.stop();
    }
  }

  protected runAnimation(): void {
    this.runAnimationBody();
  }

  private startNewQuadrant() {
    this.fadeAllTiles(1, 0);
    this.resetAllTiles(true);
  }

  protected resetAnimation(): void {
    this.blocker.x = this.blockerX;
    this.blocker.y = this.blockerY;
    this.blocker.alpha = 0;
    this.blocker.width = this.blockerW;
    this.blocker.height = this.blockerH;
    if (this.intLayer <= this.intLayersInGame) {
      UIUtil.tween(
        this.blocker,
        'y',
        this.blockerY,
        this.animMs * 5,
        undefined,
        this.startNewQuadrant.bind(this),
      );
      this.fadeAllTiles(0, 0);
    } else {
      if (this.intLayer < this.intLayersInGame) {
        this.resetAllTiles(true);
      } else {
        this.resetAllTiles(false);
      }
    }
  }

  /* -----------------------------------------------------------------------
   * Protected hooks: per-game differences, no shared default (abstract) or a
   * rules-wide default that both RulesAddSub and RulesMulDiv can rely on.
   * ----------------------------------------------------------------------- */

  protected abstract get GPR(): number[];
  protected abstract get rounds(): number[];
  protected abstract computeGameFlag(
    gameFrame: GameFrame,
    moduleVariant: number,
  ): number;
  protected abstract computeBaseGameFlag(gameFlag: number): number;
  protected abstract computeOperation(gameFlag: number): number;
  protected abstract initGameFlag(): void;

  protected onRulesFramesSet(): void {}

  protected abstract initialUiSetup(): void;

  protected initButtons(): void {}

  protected onSetupGameHook(): void {}

  protected onStopGameBegin(): void {}

  protected abstract startGameBody(): void;

  protected onNextProblemFirstTime(): void {}

  protected abstract populateProblem(): void;

  protected abstract fillGridCell(
    row: number,
    col: number,
    selectedIndex: number,
  ): void;

  protected abstract finalizeNextProblem(): void;

  protected abstract isAltFrameGameFlag(gameFlag: number): boolean;

  protected onDragStart(): void {}

  protected dragMoveBringToFrontTarget(): createjs.DisplayObject {
    return this.mobilePiece.parent;
  }

  protected canStartDrag(): boolean {
    return true;
  }

  protected abstract getMouseCoordinatesHook(e: createjs.MouseEvent): {
    row: number;
    col: number;
  };

  protected abstract isPointOutOfBounds(point: {
    x: number;
    y: number;
  }): boolean;

  protected abstract computeHoverCell(point: { x: number; y: number }): {
    row: number | null;
    col: number | null;
  };

  protected abstract onHoverCellChange(
    tRow: number | null,
    tCol: number | null,
  ): void;

  protected canTradePieceHook(): boolean {
    return true;
  }

  protected abstract onCorrectMatch(tValue: string | number): void;

  protected abstract playCorrectSoundHook(): void;

  protected onBonusSoundQueueHook(): void {}

  protected abstract checkForEndBody(): void;

  protected abstract runAnimationBody(): void;

  protected useEarlyEnableStartButton(): boolean {
    return false;
  }

  protected timeOutResetActivates(): boolean {
    return false;
  }
}
